import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { ExclamationTriangleIcon, CheckCircleIcon, MapPinIcon } from '@heroicons/react/24/solid';
import { devices, measurements, violations, venueManagement } from '../services/api';
import websocket from '../services/websocket';
import { useAuth } from '../contexts/AuthContext';

// Import new visualization components
import LiveSPLGauge from './visualizations/LiveSPLGauge';
import WaveformVisualizer from './visualizations/WaveformVisualizer';
import ViolationTimeline from './visualizations/ViolationTimeline';
import AmbientBackground from './visualizations/AmbientBackground';

interface Device {
  id: string;
  device_name: string;
  location: string;
  is_online: boolean;
  last_seen: string;
}

interface Measurement {
  time: string;
  laeq_instant: number;
  lc_peak: number;
  laeq_15min: number;
}

interface Violation {
  id: string;
  device_id: string;
  threshold_name: string;
  started_at: string;
  peak_level: number;
  resolved_at?: string;
}

const VenueOwnerDashboard: React.FC = () => {
  const { user } = useAuth();
  const [deviceList, setDeviceList] = useState<Device[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<string>('');
  const [latestMeasurements, setLatestMeasurements] = useState<Measurement[]>([]);
  const [currentSPL, setCurrentSPL] = useState({ laeq: 0, peak: 0 });
  const [activeViolations, setActiveViolations] = useState<Violation[]>([]);
  const [allViolations, setAllViolations] = useState<Violation[]>([]);
  const [venueName, setVenueName] = useState<string>('');
  const [loading, setLoading] = useState(true);

  // Load venue and devices on mount
  useEffect(() => {
    loadVenueData();
    websocket.connect();

    return () => {
      websocket.disconnect();
    };
  }, []);

  // Subscribe to selected device
  useEffect(() => {
    if (selectedDevice) {
      loadDeviceData(selectedDevice);
      websocket.subscribeToDevice(selectedDevice);

      // Listen for real-time updates
      const handleMeasurement = (data: any) => {
        if (data.deviceId === selectedDevice) {
          setCurrentSPL({
            laeq: data.measurements.laeq_instant,
            peak: data.measurements.lc_peak,
          });

          // Add to chart data
          setLatestMeasurements(prev => {
            const newData = [...prev, {
              time: new Date().toISOString(),
              laeq_instant: data.measurements.laeq_instant,
              lc_peak: data.measurements.lc_peak,
              laeq_15min: data.measurements.laeq_15min,
            }];
            // Keep only last 100 points
            return newData.slice(-100);
          });
        }
      };

      const handleViolation = (data: any) => {
        if (data.deviceId === selectedDevice) {
          setActiveViolations(prev => [data, ...prev]);
          setAllViolations(prev => [data, ...prev]);
        }
      };

      websocket.on('measurements', handleMeasurement);
      websocket.on('violation', handleViolation);

      return () => {
        websocket.off('measurements', handleMeasurement);
        websocket.off('violation', handleViolation);
      };
    }
  }, [selectedDevice]);

  const loadVenueData = async () => {
    try {
      // Get venue owner's venues
      const venuesResponse = await venueManagement.getMyVenues();
      if (venuesResponse.data.length > 0) {
        const venue = venuesResponse.data[0]; // Venue owners typically have one venue
        setVenueName(venue.venue_name);

        // Load devices for this venue
        const response = await devices.list();
        setDeviceList(response.data);
        if (response.data.length > 0 && !selectedDevice) {
          setSelectedDevice(response.data[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to load venue data', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDeviceData = async (deviceId: string) => {
    try {
      // Load recent measurements
      const measResponse = await measurements.getLatest(deviceId, 100);
      setLatestMeasurements(measResponse.data);

      if (measResponse.data.length > 0) {
        const latest = measResponse.data[0];
        setCurrentSPL({
          laeq: latest.laeq_instant,
          peak: latest.lc_peak,
        });
      }

      // Load active violations
      const violResponse = await violations.list({
        device_id: deviceId,
        resolved: false,
      });
      setActiveViolations(violResponse.data);

      // Load all violations for timeline (last 24h)
      const allViolResponse = await violations.list({
        device_id: deviceId,
      });
      setAllViolations(allViolResponse.data);
    } catch (error) {
      console.error('Failed to load device data', error);
    }
  };

  const getComplianceStatus = (): 'compliant' | 'warning' | 'violation' => {
    if (activeViolations.length === 0) return 'compliant';
    if (activeViolations.length <= 2) return 'warning';
    return 'violation';
  };

  const selectedDeviceData = deviceList.find(d => d.id === selectedDevice);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <motion.div
          className="rounded-full h-16 w-16 border-4 border-indigo-600 border-t-transparent"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        />
      </div>
    );
  }

  const complianceStatus = getComplianceStatus();
  const statusConfig = {
    compliant: {
      text: 'All Clear',
      color: 'text-green-600 dark:text-green-400',
      icon: CheckCircleIcon,
      bgColor: 'bg-green-100 dark:bg-green-900/30'
    },
    warning: {
      text: 'Warning',
      color: 'text-yellow-600 dark:text-yellow-400',
      icon: ExclamationTriangleIcon,
      bgColor: 'bg-yellow-100 dark:bg-yellow-900/30'
    },
    violation: {
      text: 'Violation',
      color: 'text-red-600 dark:text-red-400',
      icon: ExclamationTriangleIcon,
      bgColor: 'bg-red-100 dark:bg-red-900/30'
    },
  };

  const status = statusConfig[complianceStatus];
  const StatusIcon = status.icon;

  // Extract SPL values for waveform
  const waveformData = latestMeasurements.map(m => m.laeq_instant);

  return (
    <div className="relative min-h-screen">
      {/* Ambient Background */}
      <AmbientBackground
        complianceStatus={complianceStatus}
        intensity={activeViolations.length > 0 ? 0.7 : 0.3}
      />

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                {venueName || 'Live Sound Monitoring'}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Real-time acoustic compliance monitoring
              </p>
            </div>

            {/* Status Badge */}
            <motion.div
              className={`flex items-center gap-3 ${status.bgColor} rounded-full px-6 py-3 shadow-lg`}
              whileHover={{ scale: 1.05 }}
            >
              <StatusIcon className={`h-7 w-7 ${status.color}`} />
              <div>
                <div className="text-xs text-gray-600 dark:text-gray-400">Status</div>
                <div className={`text-lg font-bold ${status.color}`}>{status.text}</div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Device Selector */}
        {deviceList.length > 1 && (
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-xl p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-3">
                <MapPinIcon className="h-5 w-5 text-gray-500" />
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Monitoring Location
                </label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {deviceList.map(device => (
                  <motion.button
                    key={device.id}
                    onClick={() => setSelectedDevice(device.id)}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      selectedDevice === device.id
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="text-left">
                        <div className="font-semibold text-gray-900 dark:text-gray-100">
                          {device.location || device.device_name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {device.device_name}
                        </div>
                      </div>
                      <div className={`w-3 h-3 rounded-full ${device.is_online ? 'bg-green-500' : 'bg-gray-400'}`} />
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Main Visualization Area */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Live SPL Gauges */}
          <motion.div
            className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-xl p-8 border border-gray-200 dark:border-gray-700"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6 text-center">
              Current Sound Levels
            </h3>
            <div className="flex justify-center items-center">
              <LiveSPLGauge
                value={currentSPL.laeq}
                peakValue={currentSPL.peak}
                label="LAeq (Average)"
                size={320}
              />
            </div>
            {selectedDeviceData && (
              <div className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
                <div className="font-medium">{selectedDeviceData.location || selectedDeviceData.device_name}</div>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <div className={`w-2 h-2 rounded-full ${selectedDeviceData.is_online ? 'bg-green-500' : 'bg-gray-400'}`} />
                  <span>{selectedDeviceData.is_online ? 'Online' : 'Offline'}</span>
                  {selectedDeviceData.is_online && selectedDeviceData.last_seen && (
                    <span>• Last update: {format(new Date(selectedDeviceData.last_seen), 'HH:mm:ss')}</span>
                  )}
                </div>
              </div>
            )}
          </motion.div>

          {/* Peak Level Gauge */}
          <motion.div
            className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-xl p-8 border border-gray-200 dark:border-gray-700"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6 text-center">
              Peak Levels
            </h3>
            <div className="flex justify-center items-center">
              <LiveSPLGauge
                value={currentSPL.peak}
                label="LC Peak"
                size={320}
              />
            </div>
            <div className="mt-6 grid grid-cols-3 gap-4 text-center text-sm">
              <div>
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {waveformData.filter(v => v < 85).length}
                </div>
                <div className="text-gray-500 dark:text-gray-400">Safe</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                  {waveformData.filter(v => v >= 85 && v < 95).length}
                </div>
                <div className="text-gray-500 dark:text-gray-400">Caution</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {waveformData.filter(v => v >= 95).length}
                </div>
                <div className="text-gray-500 dark:text-gray-400">High</div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Waveform Visualization */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-xl p-6 border border-gray-200 dark:border-gray-700">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Live Sound Waveform
            </h3>
            <div className="flex justify-center">
              <WaveformVisualizer
                data={waveformData}
                width={Math.min(window.innerWidth - 100, 1000)}
                height={180}
                showGradient={true}
                animated={true}
              />
            </div>
          </div>
        </motion.div>

        {/* Violation Timeline */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <ViolationTimeline violations={allViolations} timeRange={24} />
        </motion.div>

        {/* Help Section */}
        <motion.div
          className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900 rounded-xl p-6 border border-blue-200 dark:border-gray-700"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <span className="text-2xl">📊</span>
            Understanding Your Dashboard
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <p className="font-semibold text-gray-900 dark:text-gray-100">Safe Levels (&lt; 85 dB)</p>
              </div>
              <p className="text-gray-600 dark:text-gray-400">
                Sound levels are within acceptable limits. No action needed.
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <p className="font-semibold text-gray-900 dark:text-gray-100">Caution (85-95 dB)</p>
              </div>
              <p className="text-gray-600 dark:text-gray-400">
                Approaching threshold limits. Monitor closely and consider reducing volume.
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <p className="font-semibold text-gray-900 dark:text-gray-100">Too Loud (&gt; 95 dB)</p>
              </div>
              <p className="text-gray-600 dark:text-gray-400">
                Exceeds safe limits. Reduce volume immediately to maintain compliance.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default VenueOwnerDashboard;
